import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, LinkIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { EmailBuilder } from '../components/builder/EmailBuilder';
import { EmailBlock, EmailGlobalStyles, EmailBuilderSchema, DEFAULT_EMAIL_STYLES } from '../components/builder/types';
import { emailTemplateLibraryApi, EmailLibraryRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { uploadImageToHost, IMAGE_UPLOAD_DISABLED, IMAGE_UPLOAD_DISABLED_MESSAGE } from '../utils/api';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
const CATEGORIES = ['Confirmation', 'Welcome', 'Notification', 'Newsletter', 'Other'];
export function EmailTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [templateData, setTemplateData] = useState({
    subject: '',
    preheader: '',
    html: ''
  });
  const [emailBuilderBlocks, setEmailBuilderBlocks] = useState<EmailBlock[]>([]);
  const [emailBuilderStyles, setEmailBuilderStyles] = useState<EmailGlobalStyles>(DEFAULT_EMAIL_STYLES);
  // Image hosting state
  const [isHostingImages, setIsHostingImages] = useState(false);
  const [hostingProgress, setHostingProgress] = useState('');
  const base64ImageCount = (templateData.html.match(/src=["']data:image\/[^"']+["']/gi) || []).length;
  const handleHostImages = async () => {
    if (IMAGE_UPLOAD_DISABLED) {
      toast.error('Image hosting unavailable', {
        description: IMAGE_UPLOAD_DISABLED_MESSAGE,
        duration: 6000
      });
      return;
    }
    const html = templateData.html;
    const base64Regex = /src=["'](data:image\/[^"']+)["']/gi;
    const matches: {
      full: string;
      dataUrl: string;
    }[] = [];
    let match;
    while ((match = base64Regex.exec(html)) !== null) {
      matches.push({
        full: match[0],
        dataUrl: match[1]
      });
    }
    if (matches.length === 0) {
      toast.info('No embedded base64 images found in the HTML');
      return;
    }
    setIsHostingImages(true);
    let updatedHtml = html;
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < matches.length; i++) {
      const {
        full,
        dataUrl
      } = matches[i];
      setHostingProgress(`Uploading image ${i + 1} of ${matches.length}...`);
      try {
        const typeMatch = dataUrl.match(/data:image\/(\w+)/);
        const ext = typeMatch ? typeMatch[1].replace('jpeg', 'jpg') : 'png';
        const filename = `email-lib-img-${Date.now()}-${i + 1}.${ext}`;
        const hostedUrl = await uploadImageToHost(dataUrl, filename);
        const quote = full.includes('"') ? '"' : "'";
        updatedHtml = updatedHtml.replace(full, `src=${quote}${hostedUrl}${quote}`);
        successCount++;
      } catch (err) {
        failCount++;
        console.warn(`Failed to host image ${i + 1}:`, err);
      }
    }
    setTemplateData(prev => ({
      ...prev,
      html: updatedHtml
    }));
    setIsHostingImages(false);
    setHostingProgress('');
    if (successCount > 0 && failCount === 0) {
      const saved = html.length - updatedHtml.length;
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} hosted successfully`, {
        description: `Saved ~${saved.toLocaleString()} characters. Remember to save.`,
        duration: 5000
      });
    } else if (successCount > 0) {
      toast.warning(`${successCount} hosted, ${failCount} failed`);
    } else {
      toast.error('Failed to host images', {
        description: 'The hosting server may not be available.',
        duration: 6000
      });
    }
  };
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      emailTemplateLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setTemplateData({
            subject: record.Email_Subject || '',
            preheader: record.Preheader_Text || '',
            html: record.Email_HTML_Body || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: EmailBuilderSchema = JSON.parse(record.Builder_Schema);
              setEmailBuilderBlocks(schema.blocks || []);
              setEmailBuilderStyles(schema.globalStyles || DEFAULT_EMAIL_STYLES);
              setEditorMode('builder');
            } catch {
              /* fallback to code */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/email-templates-library');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      Email_HTML_Body: templateData.html,
      Email_Subject: templateData.subject,
      Preheader_Text: templateData.preheader,
      Builder_Mode: editorMode,
      Team: team
    };
    if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: emailBuilderBlocks,
        globalStyles: emailBuilderStyles
      });
    }
    try {
      if (isEditing && id) {
        await emailTemplateLibraryApi.update(id, payload);
        toast.success('Email template updated');
      } else {
        await emailTemplateLibraryApi.create(payload);
        toast.success('Email template created');
        navigate('/email-templates-library');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-3977" data-mp="98d4uuvzl6so">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3978" data-mp="wx4misbya6r7" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-3979" data-mp="10xya1klhdjjk">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3980" data-mp="n6ti74octvjs">
      <div className="flex items-center justify-between" data-id="element-3981" data-mp="n5ie13qzyls5">
        <div className="flex items-center gap-4" data-id="element-3982" data-mp="s4ldm8o7jzsq">
          <Link to="/email-templates-library" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3983" data-mp="s30fk92opyq4">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-3984" data-mp="1klxq9aytdiwd" />
          </Link>
          <div data-id="element-3985" data-mp="1kn8s358p8gku">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3986" data-mp="3goahf2hdtd9s">
              {isEditing ? 'Edit Email Template' : 'Create Email Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3987" data-mp="3bwj0zkrxwnxd">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your email.' : 'Write email HTML for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3988" data-mp="3ik50j4yx2jv">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-3989" data-mp="3h90ui7m1ss8">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3990" data-mp="3uov0lzr7lb3t">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && templateData.html && emailBuilderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3991" data-mp="2ebemwhp1qw0e">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3992" data-mp="18j9y6e33m2q4">
            <EyeIcon className="h-4 w-4" data-id="element-3993" data-mp="1mgyk7p8wjfwr" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3994" data-mp="3j904uvbao06h">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-3995" data-mp="3cvd5qa6i4uev" /> : <SaveIcon className="h-4 w-4" data-id="element-3996" data-mp="3hsvd3w4vf1kr" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-3997" data-mp="3rrub8ks0feti">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-3998" data-mp="3vse8docl2ifn">
            <div className="grid grid-cols-2 gap-4" data-id="element-3999" data-mp="4ltp4qsrset6">
              <div data-id="element-4000" data-mp="4kii8i412qxu">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4001" data-mp="4jqolctzr3a7">
                  Email Subject
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
              ...templateData,
              subject: e.target.value
            })} placeholder="Email subject line" data-id="element-4002" data-mp="ebd59op3lukc" />
              </div>
              <div data-id="element-4003" data-mp="1yuqh9urh7x4l">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4004" data-mp="1yu78b3jt0j2p">
                  Preheader Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
              ...templateData,
              preheader: e.target.value
            })} placeholder="Brief summary..." data-id="element-4005" data-mp="2ricu7tmlge44" />
              </div>
            </div>
          </div>

          <EmailBuilder initialBlocks={emailBuilderBlocks} initialStyles={emailBuilderStyles} onSchemaChange={(blocks, globalStyles) => {
        setEmailBuilderBlocks(blocks);
        setEmailBuilderStyles(globalStyles);
      }} onHtmlChange={html => {
        setTemplateData(prev => ({
          ...prev,
          html
        }));
      }} mergeVariables={['confirmation_link', 'unsubscribe_link', 'team_name', 'first_name', 'last_name', 'email']} data-id="element-4006" data-mp="35gja5url7tfi" />

          <div className="grid grid-cols-3 gap-6" data-id="element-4007" data-mp="awo7u8fo8hj4">
            <div className="col-span-2" data-id="element-4008" data-mp="ay95i6xa8gl7" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-4009" data-mp="fu7qdkjejggv" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-4010" data-mp="2kakyua7289hb">
          <div className="col-span-2 space-y-6" data-id="element-4011" data-mp="2fex8hrgudaa8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-id="element-4012" data-mp="2fc1cw9sguxoy">
              <div className="grid grid-cols-2 gap-4" data-id="element-4013" data-mp="2t7djbefziclp">
                <div className="space-y-2" data-id="element-4014" data-mp="2odhnoin711tp">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4015" data-mp="2ocg05eozqmv3">
                    Email Subject
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
                ...templateData,
                subject: e.target.value
              })} placeholder="Email subject line" data-id="element-4016" data-mp="2y4vhqawdl9fg" />
                </div>
                <div className="space-y-2" data-id="element-4017" data-mp="1f5efj7y8pdrx">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4018" data-mp="1k495ctumk2lj">
                    Preheader Text
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
                ...templateData,
                preheader: e.target.value
              })} placeholder="Brief summary..." data-id="element-4019" data-mp="1o6vqyd0pcraa" />
                </div>
              </div>
              <CodeEditor label="Email HTML Body" language="html" className="min-h-[400px]" charLimit={true} value={templateData.html} onChange={e => setTemplateData({
            ...templateData,
            html: e.target.value
          })} data-id="element-4020" data-mp="cryzyxkyjs0a" />

              {/* Host Embedded Images action */}
              {base64ImageCount > 0 && <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-4021" data-mp="3sbce8165f49e">
                  <div className="flex-1 min-w-0" data-id="element-4022" data-mp="3nem0hkhtgkr5">
                    <p className="text-sm font-medium text-amber-900" data-id="element-4023" data-mp="3imawo6jygetb">
                      {base64ImageCount} embedded image
                      {base64ImageCount > 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-4024" data-mp="38pg3m36gb6mf">
                      {isHostingImages ? hostingProgress : 'Base64 images bloat your HTML. Upload them to get short hosted URLs instead.'}
                    </p>
                  </div>
                  <button onClick={handleHostImages} disabled={isHostingImages} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-60 whitespace-nowrap transition-colors" data-id="element-4025" data-mp="2pzb7v641kv3v">
                    {isHostingImages ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4026" data-mp="38qc7lwczkuoh" /> : <LinkIcon className="h-4 w-4" data-id="element-4027" data-mp="3dpyjo2h2w4vp" />}
                    {isHostingImages ? 'Hosting...' : 'Host Images'}
                  </button>
                </div>}
            </div>
            <GptCodeAssistant mode="email" currentCode={{
          html: templateData.html
        }} onCodeGenerated={code => {
          if (code.html) {
            setTemplateData(prev => ({
              ...prev,
              html: code.html
            }));
          }
        }} data-id="element-4028" data-mp="33yklby0v5b45" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-4029" data-mp="1g1uqg0jydt14" />
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Template Preview" data-id="element-4030" data-mp="x38lubp89jsg">
        <EmailPreview subject={templateData.subject} html={templateData.html} data-id="element-4031" data-mp="1b39vognh01k2" />
      </PreviewModal>

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4032" data-mp="162vot1xluxnm" />
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
}) {
  return <div className="space-y-6" data-id="element-4033" data-mp="3ub6zz7kao522">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4034" data-mp="3u7rg7rfoe7ub">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4035" data-mp="23z1t2bvpjp9">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-4036" data-mp="23x6fk7buft91">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4037" data-mp="23yhlacounb2s">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Email Template" data-id="element-4038" data-mp="1wfhyry63d3f7" />
        </div>
        <div className="space-y-2" data-id="element-4039" data-mp="1ikpdsk7fmv18">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4040" data-mp="2fd1efkn21kal">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-4041" data-mp="2aerdetenfph2" />
        </div>
        <div className="space-y-2" data-id="element-4042" data-mp="uuldomtgddez">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4043" data-mp="pxldgsjab4vs">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-4044" data-mp="3mfsmc77ifumw">
            <option value="" data-id="element-4045" data-mp="3dk2vvjr7ga2n">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-4046" data-mp="38l9kpxze4v5n">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-4047" data-mp="56x2r802mu20">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4048" data-mp="57yn632z8swe">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4049" data-mp="5zi8orl9nxc" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4050" data-mp="2fyyh0eghme4m">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4051" data-mp="25v08pdv4exo1">
          Available Variables
        </h3>
        <p className="text-xs text-gray-500" data-id="element-4052" data-mp="2auwdvgfk0v56">
          Use these merge tags in your email HTML. They'll be replaced with
          actual values when the email is sent.
        </p>
        <div className="flex flex-wrap gap-1.5" data-id="element-4053" data-mp="2kk67x1tyknvx">
          {['confirmation_link', 'unsubscribe_link', 'team_name', 'first_name', 'last_name', 'email'].map(v => <button key={v} onClick={() => {
          const text = `{{${v}}}`;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            toast.success(`Copied ${text}`, {
              duration: 1500
            });
          }
        }} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100" title={`Copy {{${v}}}`} data-id="element-4054" data-mp="2tihw6x7cr9l7">
              {`{{${v}}}`}
            </button>)}
        </div>
      </div>
    </div>;
}